import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { WORD_API_URL, DOZEN_API_URL, PROFILE_PATH } from '../config';
import AppShell from './AppShell';
import GameSwitch from './GameSwitch';
import { BadgeIcon, BadgeIconPerfect } from './BadgeIcons';
import { DozenFullBadge, DozenIconFastest, DozenTargetMedal } from './DozenHallIcons';
import { calendarDateFromSql, dateFromSqlUtc, formatRuDate } from '../utils/dates';
import { DOZEN_APP, WORD_APP } from '../apps';
import { profileExitPath, profileSections, rememberProfileGame } from '../utils/profileNav';
import './Leaderboard.css';
import './Profile.css';

function formatTime(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  const rounded = Math.max(0, Math.round(seconds));
  if (rounded < 60) return `${rounded}с`;
  const minutes = Math.floor(rounded / 60);
  const secs = rounded % 60;
  return `${minutes}м ${secs}с`;
}

function positionText(position) {
  if (position === 1) return '1-я';
  if (position === 2) return '2-я';
  if (position === 3) return '3-я';
  return `${position}-я`;
}

function MedalRow({ counts, keys, Badge }) {
  return (
    <div className="badges-grid badges-grid--medals">
      {keys.map((key, index) => (
        <div key={key} className="badge-chip">
          <Badge place={index + 1} />
          <div className="badge-chip__count">{counts?.[key] ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function SingleBadge({ icon, count }) {
  return (
    <div className="badges-grid badges-grid--single">
      <div className="badge-chip">
        {icon}
        <div className="badge-chip__count">{count}</div>
      </div>
    </div>
  );
}

function DozenNominations({ counts }) {
  return (
    <div className="badges-section">
      <h3>Номинации недели</h3>
      <h4 className="badges-subtitle">В яблочко</h4>
      <MedalRow counts={counts} keys={['score1', 'score2', 'score3']} Badge={DozenTargetMedal} />
      <h4 className="badges-subtitle">Быстрее пули</h4>
      <MedalRow counts={counts} keys={['fastest1', 'fastest2', 'fastest3']} Badge={DozenIconFastest} />
      <h4 className="badges-subtitle">Полная обойма</h4>
      <MedalRow counts={counts} keys={['full1', 'full2', 'full3']} Badge={DozenFullBadge} />
      <h4 className="badges-subtitle">В тир, как на работу</h4>
      <SingleBadge icon={<BadgeIconPerfect />} count={counts?.regulars ?? 0} />
    </div>
  );
}

function Profile() {
  const { username, game } = useParams();
  const section = game === 'dozen' ? 'dozen' : game === 'word' ? 'word' : null;
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isOwnProfile =
    Boolean(user?.username) &&
    user.username.toLowerCase() === String(username || '').toLowerCase();

  useEffect(() => {
    if (section) rememberProfileGame(section);
  }, [section]);

  useEffect(() => {
    if (!section) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const url =
          section === 'dozen'
            ? `${DOZEN_API_URL}/profile/${username}`
            : `${WORD_API_URL}/profile/${username}`;
        const { data } = await axios.get(url);
        if (!cancelled) setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [username, section]);

  if (!section) {
    return <Navigate to={`${PROFILE_PATH}/${username}/word`} replace />;
  }

  const shell = (children) => (
    <AppShell
      app={section === 'dozen' ? DOZEN_APP : WORD_APP}
      title="Профиль"
      showBack
      backTo={profileExitPath()}
      hideTabs
    >
      <GameSwitch sections={profileSections(username)} />
      {children}
    </AppShell>
  );

  if (loading) {
    return shell(<div className="loading">Загрузка...</div>);
  }

  if (!profile) {
    return shell(
      <div className="profile-error">
        Пользователь не найден или произошла ошибка при загрузке профиля
      </div>
    );
  }

  const positions = profile.stats?.positions || {};
  const formatDate = (sqlUtc) => formatRuDate(dateFromSqlUtc(sqlUtc));
  const formatGameDate = (gameRow) =>
    formatRuDate(
      calendarDateFromSql(gameRow.word_date || gameRow.published_at) ||
        dateFromSqlUtc(gameRow.created_at || gameRow.finished_at)
    );
  const renderGame = (row, highlightScore) => (
    <div key={row.id} className={`game-item${highlightScore ? ' best-game' : ''}`}>
      <div className="game-word">
        {section === 'dozen' ? `${row.solved_count} из 12` : row.word || 'Текущее слово'}
      </div>
      <div className="game-details">
        <span>{positionText(row.position)}</span>
        <span className={highlightScore ? 'highlight' : undefined}>{row.score} очк.</span>
        <span>{formatTime(section === 'dozen' ? row.total_time_ms / 1000 : row.time_seconds)}</span>
        <span>{formatGameDate(row)}</span>
      </div>
    </div>
  );

  return shell(
    <div className="profile-content">
      <div className="profile-hero">
        <h2 className="profile-title">{profile.user.username}</h2>
        <p className="profile-joined">Участник с {formatDate(profile.user.createdAt)}</p>
      </div>

      {section === 'dozen' && <DozenNominations counts={profile.nominations} />}

      {section === 'word' && (
        <div className="badges-section">
          <h3>Награды недели</h3>
          <h4 className="badges-subtitle">Самые Гениальные</h4>
          <div className="badges-grid badges-grid--medals">
            {['podium1', 'podium2', 'podium3'].map((category) => (
              <div key={category} className="badge-chip">
                <BadgeIcon category={category} />
                <div className="badge-chip__count">{profile.badges?.counts?.[category] ?? 0}</div>
              </div>
            ))}
          </div>
          <h4 className="badges-subtitle">Самые Быстрые</h4>
          <div className="badges-grid badges-grid--medals">
            {['fastest1', 'fastest2', 'fastest3'].map((category) => (
              <div key={category} className="badge-chip">
                <BadgeIcon category={category} />
                <div className="badge-chip__count">{profile.badges?.counts?.[category] ?? 0}</div>
              </div>
            ))}
          </div>
          <h4 className="badges-subtitle">Завсегдатаи</h4>
          <div className="badges-grid badges-grid--single">
            <div className="badge-chip">
              <BadgeIcon category="perfect" />
              <div className="badge-chip__count">{profile.badges?.counts?.perfect ?? 0}</div>
            </div>
          </div>
        </div>
      )}

      {profile.bestGames?.length > 0 && (
        <div className="games-section">
          <h3>Лучшая игра</h3>
          <div className="games-list">
            {profile.bestGames.slice(0, 1).map((row) => renderGame(row, true))}
          </div>
        </div>
      )}

      <div className="positions-section">
        <h3>Места за день</h3>
        <div className="positions-grid">
          <div className="position-item">
            <div className="position-value">{positions.firstPlace || 0}</div>
            <div className="position-label">1-е</div>
          </div>
          <div className="position-item">
            <div className="position-value">{positions.secondPlace || 0}</div>
            <div className="position-label">2-е</div>
          </div>
          <div className="position-item">
            <div className="position-value">{positions.thirdPlace || 0}</div>
            <div className="position-label">3-е</div>
          </div>
          <div className="position-item">
            <div className="position-value">{positions.avgPosition || '-'}</div>
            <div className="position-label">Среднее</div>
          </div>
        </div>
      </div>

      {profile.recentGames?.length > 0 && (
        <div className="games-section">
          <h3>Последние игры</h3>
          <div className="games-list">
            {profile.recentGames.slice(0, 3).map((row) => renderGame(row, false))}
          </div>
        </div>
      )}

      {isOwnProfile && (
        <button type="button" className="btn btn-secondary profile-logout" onClick={logout}>
          Выйти
        </button>
      )}
    </div>
  );
}

export default Profile;
